import React, { useState } from 'react';
import { Currency, formatMoney } from '../../common/format';
import { RequestResponseViewer } from '../../common/request-response-viewer';
import {
  COMMON_MONTHLY_PROJECTION_COLUMNS,
  COMMON_PROJECTION_COLUMNS,
  ChartSeriesConfig,
  CurrencyHeader,
  EvolutionChart,
  KpiCard,
  KpiGrid,
  ProjectionColumn,
  ProjectionTable,
  SimulationWarnings,
  buildEvolutionSeries,
  buildMonthlyProjectionColumns,
  buildProjectionColumns,
} from '../_shared';
import {
  FamilySavingsInsuranceResponse,
  FamilySavingsMonthlyPolicyRow,
  FamilySavingsPolicyRow,
} from './service';

const CHART_SERIES: ChartSeriesConfig[] = [
  { dataKey: 'policy_value', name: 'Valor de la póliza', stroke: 'var(--bs-primary)' },
  { dataKey: 'accumulated_prime', name: 'Prima acumulada', stroke: 'var(--bs-gray-600)' },
  {
    dataKey: 'death_compensation',
    name: 'Indemnización fallecimiento',
    stroke: 'var(--bs-success)',
    strokeDasharray: '5 4',
  },
  { dataKey: 'withdrawal', name: 'Retiro', stroke: 'var(--bs-warning)' },
];

const MONTHLY_CHART_SERIES: ChartSeriesConfig[] = [
  { dataKey: 'policy_value', name: 'Valor de la póliza', stroke: 'var(--bs-primary)' },
  { dataKey: 'withdrawal', name: 'Retiro', stroke: 'var(--bs-warning)' },
];

const WITHDRAWAL_COLUMNS: ProjectionColumn<FamilySavingsPolicyRow>[] = [
  { id: 'withdrawal', label: 'Retiro', isMoney: true },
  { id: 'policy_value_before_withdrawal', label: 'VP pre-retiro', isMoney: true },
  { id: 'policy_value_after_withdrawal', label: 'VP post-retiro', isMoney: true },
];

const WITHDRAWAL_MONTHLY_COLUMNS: ProjectionColumn<FamilySavingsMonthlyPolicyRow>[] = [
  { id: 'withdrawal', label: 'Retiro', isMoney: true },
  { id: 'policy_value_before_withdrawal', label: 'VP pre-retiro', isMoney: true },
  { id: 'policy_value_after_withdrawal', label: 'VP post-retiro', isMoney: true },
];

const MODES_WITH_WITHDRAWALS = new Set(['goal_value', 'withdrawal_goal']);

function isRequestWithWithdrawals(request: unknown): boolean {
  if (typeof request !== 'object' || request === null) return false;
  const mode = (request as { savings_mode?: unknown }).savings_mode;
  return typeof mode === 'string' && MODES_WITH_WITHDRAWALS.has(mode);
}

interface FamilySavingsInsuranceResultViewProps {
  request: unknown;
  response?: FamilySavingsInsuranceResponse;
  error?: unknown;
}

export function FamilySavingsInsuranceResultView({
  request,
  response,
  error,
}: FamilySavingsInsuranceResultViewProps) {
  const [currency, setCurrency] = useState<Currency>('UF');
  const hasSuccess = !!response && !error;
  const hasWithdrawals = isRequestWithWithdrawals(request);
  const yearlyColumns = hasWithdrawals
    ? buildProjectionColumns<FamilySavingsPolicyRow>(WITHDRAWAL_COLUMNS)
    : COMMON_PROJECTION_COLUMNS;
  const monthlyColumns = hasWithdrawals
    ? buildMonthlyProjectionColumns<FamilySavingsMonthlyPolicyRow>(WITHDRAWAL_MONTHLY_COLUMNS)
    : COMMON_MONTHLY_PROJECTION_COLUMNS;
  const yearlySeries = hasWithdrawals ? CHART_SERIES : CHART_SERIES.slice(0, 3);
  const monthlySeries = hasWithdrawals ? MONTHLY_CHART_SERIES : MONTHLY_CHART_SERIES.slice(0, 1);
  const yearlyKeys = hasWithdrawals
    ? ['policy_value', 'accumulated_prime', 'death_compensation', 'withdrawal']
    : ['policy_value', 'accumulated_prime', 'death_compensation'];
  const monthlyKeys = hasWithdrawals ? ['policy_value', 'withdrawal'] : ['policy_value'];

  return (
    <div className="mt-4">
      {hasSuccess && response && (
        <>
          <CurrencyHeader
            ufValue={response.uf_value}
            currency={currency}
            onCurrencyChange={setCurrency}
          />

          <SimulationWarnings warnings={response.warnings} />

          <div className="mb-4">
            <KpiGrid>
              <KpiCard
                label="Ahorro bruto"
                value={formatMoney(response.savings.gross, currency, response.uf_value)}
                hint={`${response.timeframe_months} meses`}
                accent="text-primary"
              />
              <KpiCard
                label="Ahorro neto"
                value={formatMoney(response.savings.net, currency, response.uf_value)}
                accent="text-success"
              />
              <KpiCard
                label="Ganado por interés"
                value={formatMoney(response.savings.interest, currency, response.uf_value)}
              />
              <KpiCard
                label="Prima acordada"
                value={formatMoney(response.premium.agreed, currency, response.uf_value)}
              />
              <KpiCard
                label="Prima básica"
                value={formatMoney(response.premium.basic, currency, response.uf_value)}
              />
              <KpiCard
                label="Prima excedente"
                value={formatMoney(response.premium.excess, currency, response.uf_value)}
                accent="text-danger"
              />
              {response.terminal_excess !== undefined && (
                <KpiCard
                  label="Excedente terminal"
                  value={formatMoney(response.terminal_excess, currency, response.uf_value)}
                  hint="Saldo VP tras el último retiro"
                  accent="text-info"
                />
              )}
            </KpiGrid>
          </div>

          <div className="mb-4">
            <EvolutionChart
              data={buildEvolutionSeries(
                response.projection_by_year,
                yearlyKeys,
                currency,
                response.uf_value,
              )}
              series={yearlySeries}
              currency={currency}
            />
          </div>

          {response.projection_by_month && (
            <>
              <div className="mb-4">
                <EvolutionChart
                  title="Evolución mensual de la póliza"
                  data={buildEvolutionSeries(
                    response.projection_by_month,
                    monthlyKeys,
                    currency,
                    response.uf_value,
                    ['month'],
                  )}
                  series={monthlySeries}
                  currency={currency}
                  xKey="month"
                  xLabel="Mes"
                />
              </div>

              <div className="mb-4">
                <ProjectionTable
                  rows={response.projection_by_month}
                  columns={monthlyColumns}
                  currency={currency}
                  ufValue={response.uf_value}
                  title="Detalle mensual"
                  rowKey="month"
                />
              </div>
            </>
          )}

          <div className="mb-4">
            <ProjectionTable
              rows={response.projection_by_year}
              columns={yearlyColumns}
              currency={currency}
              ufValue={response.uf_value}
            />
          </div>
        </>
      )}

      <RequestResponseViewer request={request} response={response} error={error} />
    </div>
  );
}
